import type { AnsweredQuestionnaire } from "@9amhealth/openapi";
import { useCallback, useEffect } from "react";
import {
  PTHW_BINARY_RATING_QUESTIONNAIRE_ID,
  RISK_QUIZ_QUESTIONNAIRE_ID
} from "src/constants/misc";
import createTrackEvent from "src/lib/createTrackEvent";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { toast, tracker } from "src/state/state";
import type { Answer } from "src/ui/components/Feedback/Feedback";

const processedEvents = new Set<string>();

export default function useQuestionnaireTracking() {
  type AnsweredQuestionnaireWithCustomVars = AnsweredQuestionnaire & {
    customFormVariables: {
      custom_score?: number;
      partner?: string;
      rating_subject_uid?: string;
      rating_subject_title?: string;
    };
  };

  const handleQuestionnaireSaved = useCallback((event: Event) => {
    const customEvent =
      event as CustomEvent<AnsweredQuestionnaireWithCustomVars>;

    const riskQuizEmailQuestionId = "iLAVtu3XKhLr";

    if (customEvent.detail.questionnaireRef.id === RISK_QUIZ_QUESTIONNAIRE_ID) {
      const answers = (customEvent.detail.answers.json ?? []) as Answer[];
      const emailQuestion = answers.find(
        (item) => item.questionId === riskQuizEmailQuestionId
      );
      const email = emailQuestion?.fieldValue;
      const score = customEvent.detail.customFormVariables.custom_score;
      const { partner } = customEvent.detail.customFormVariables;

      if (email && typeof score === "number") {
        tracker.track(createTrackEvent("Risk Quiz Completed"), {
          data: {
            "Marketing Email": email,
            Score: score,
            Partner: partner
          }
        });
      } else {
        toast.error("error_form_risk_quiz");
        reportErrorSentry(
          new Error(
            "No email and/or score provided after completing the risk quiz"
          )
        );
      }
    }

    if (
      customEvent.detail.questionnaireRef.id ===
      PTHW_BINARY_RATING_QUESTIONNAIRE_ID
    ) {
      const ratingSubjectUid =
        customEvent.detail.customFormVariables.rating_subject_uid;
      const ratingSubjectTitle =
        customEvent.detail.customFormVariables.rating_subject_title;
      const answers = (customEvent.detail.answers.json ?? []) as Answer[];
      const [ratingAnswer] = answers;
      const rating = ratingAnswer.fieldValue;

      const urlParams = new URLSearchParams(window.location.search);
      const ratingModuleName = urlParams.get("moduleName") ?? "";

      const eventKey = `${customEvent.detail.questionnaireRef.id}-${ratingSubjectUid}`;

      if (!ratingSubjectUid || !ratingSubjectTitle) {
        reportErrorSentry(
          new Error("Missing required data for module rating tracking")
        );
        return;
      }

      if (!processedEvents.has(eventKey)) {
        processedEvents.add(eventKey);

        tracker.track(createTrackEvent("Module Rating Submitted"), {
          data: {
            "Rating Subject UID": ratingSubjectUid,
            "Rating Subject Title": ratingSubjectTitle,
            "Rating Module Name": ratingModuleName,
            Rating: rating
          }
        });
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener(
      "nineQuestionnaireSaved",
      handleQuestionnaireSaved
    );

    return () => {
      document.removeEventListener(
        "nineQuestionnaireSaved",
        handleQuestionnaireSaved
      );
    };
  }, []);
}
